import React, { FC, useContext, useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
} from '@material-ui/core'
import {
  Save as SaveIcon,
  Visibility as PreviewIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@material-ui/icons'
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import { ja } from 'date-fns/locale'
import { PrimaryConfirmModal } from '../../../molecules/ModalContainer/PrimaryDialogTemplate/PrimaryConfirmDialog'
import { ModalsContext } from '../../../../contexts'
import { formatNotificationDateTime } from '../../../../libs/date'
import { generateTolOperationsItemId } from '../../../../libs/functions/tolOperationsItemId'
import { TolNotification, ValidationError } from '../../../../types/tolOperations'
import { NotificationPreviewModal } from './NotificationPreviewModal'
import {
  NOTIFICATION_STATUS_LABELS,
  NotificationPublishStatus,
  getPublishStatusFromDates,
  parseNotificationDate,
} from '../shared/periodStatus'

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  notificationCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    border: '4px solid transparent',
    borderRadius: theme.spacing(1),
  },
  notificationCardEdited: {
    borderLeftColor: theme.palette.primary.main,
  },
  notificationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  statusChips: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  formControl: {
    width: '100%',
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  addButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(6),
  },
  actionButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  loadingState: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(8),
  },
}))

/** フォーム編集用のお知らせ。日時は日時ピッカーで扱うため Date で保持する */
export interface FormNotification {
  id: string
  title: string
  url: string
  type: 'information' | 'warning'
  startDisplayAt: Date | null
  endDisplayAt: Date | null
}

export interface FieldErrors {
  title?: string
  url?: string
  startDisplayAt?: string
  endDisplayAt?: string
}

interface NotificationFormPageProps {
  existingNotifications: TolNotification[]
  isLoading: boolean
  isSaving: boolean
  serverErrors: ValidationError[]
  onClearServerErrors: () => void
  onSave: (items: TolNotification[]) => Promise<void>
  onCancel: () => void
}

const TITLE_MAX_LENGTH = 100
const URL_MAX_LENGTH = 2048
const URL_PATTERN = /^https?:\/\/.+/

const createEmptyNotification = (usedIds: Iterable<string>): FormNotification => ({
  id: generateTolOperationsItemId(usedIds),
  title: '',
  url: '',
  type: 'information',
  startDisplayAt: null,
  endDisplayAt: null,
})

const toFormNotification = (notification: TolNotification): FormNotification => ({
  id: notification.id,
  title: notification.title,
  url: notification.url,
  type: notification.type,
  startDisplayAt: parseNotificationDate(notification.startDisplayAt),
  endDisplayAt: parseNotificationDate(notification.endDisplayAt),
})

const isValidDate = (date: Date | null): date is Date => date !== null && !isNaN(date.getTime())

const ENDED_NOTIFICATION_GUIDE = '公開が終了したお知らせは保存できません。削除してください'

// 開始・終了日時から判定した公開状態。日時が未入力・不正な場合、または対象がない場合（新規行の編集前）は null
const getNotificationStatus = (
  notification: FormNotification | undefined,
  now: Date = new Date(),
): NotificationPublishStatus | null =>
  notification && isValidDate(notification.startDisplayAt) && isValidDate(notification.endDisplayAt)
    ? getPublishStatusFromDates(notification.startDisplayAt, notification.endDisplayAt, now)
    : null

/**
 * バリデーション入力チェック。エラーがなければ空オブジェクトを返す
 * - 公開終了: 編集不可のため、削除を促すエラーのみを返す
 * - 公開中: 表示期間の延長（編集前の終了日時より後への変更）を許可しない
 */
export const validateNotification = (
  notification: FormNotification,
  beforeEdit: FormNotification | undefined,
  now: Date,
): FieldErrors => {
  const beforeEditStatus = getNotificationStatus(beforeEdit, now)

  if (beforeEditStatus === 'ended') {
    return { endDisplayAt: ENDED_NOTIFICATION_GUIDE }
  }

  const itemErrors: FieldErrors = {}

  const title = notification.title.trim()
  if (!title) {
    itemErrors.title = 'タイトルは必須です'
  } else if (title.length > TITLE_MAX_LENGTH) {
    itemErrors.title = 'タイトルは100文字以内で入力してください'
  }

  const url = notification.url.trim()
  if (!url) {
    itemErrors.url = 'URLは必須です'
  } else if (url.length > URL_MAX_LENGTH) {
    itemErrors.url = 'URLは2048文字以内で入力してください'
  } else if (!URL_PATTERN.test(url)) {
    itemErrors.url = 'URLはhttp://またはhttps://で始まる形式で入力してください'
  }

  if (!notification.startDisplayAt) {
    itemErrors.startDisplayAt = '表示開始日時は必須です'
  } else if (!isValidDate(notification.startDisplayAt)) {
    itemErrors.startDisplayAt = '日付の形式が正しくありません'
  }

  if (!notification.endDisplayAt) {
    itemErrors.endDisplayAt = '表示終了日時は必須です'
  } else if (!isValidDate(notification.endDisplayAt)) {
    itemErrors.endDisplayAt = '日付の形式が正しくありません'
  } else if (notification.endDisplayAt <= now) {
    itemErrors.endDisplayAt = '表示終了日時には現在時刻より後の日時を入力してください'
  } else if (
    beforeEditStatus === 'active' &&
    beforeEdit &&
    isValidDate(beforeEdit.endDisplayAt) &&
    notification.endDisplayAt > beforeEdit.endDisplayAt
  ) {
    itemErrors.endDisplayAt = '公開中のお知らせの表示期間は延長できません。延長する場合は新しいお知らせを追加してください'
  } else if (isValidDate(notification.startDisplayAt) && notification.startDisplayAt >= notification.endDisplayAt) {
    itemErrors.endDisplayAt = '表示終了日時は表示開始日時より後である必要があります'
  }

  return itemErrors
}

const toApiNotification = (notification: FormNotification): TolNotification => ({
  id: notification.id,
  title: notification.title.trim(),
  url: notification.url.trim(),
  type: notification.type,
  startDisplayAt: formatNotificationDateTime(notification.startDisplayAt as Date),
  endDisplayAt: formatNotificationDateTime(notification.endDisplayAt as Date),
})

const DATE_INPUT_GUIDE = 'キーボード入力する場合は半角数字で入力してください'

// エラー時も案内文を消さず2行目に表示し続ける
const buildDateHelperText = (error: string | undefined): React.ReactNode =>
  error ? (
    <>
      {error}
      <Typography component="span" display="block" variant="caption" color="textSecondary">
        {DATE_INPUT_GUIDE}
      </Typography>
    </>
  ) : (
    DATE_INPUT_GUIDE
  )

// 一括削除対象のID一覧
const buildBulkDeletePreview = (items: { id: string }[]): string => items.map(item => `・${item.id}`).join('\n')

export const NotificationFormPage: FC<NotificationFormPageProps> = ({
  existingNotifications,
  isLoading,
  isSaving,
  serverErrors,
  onClearServerErrors,
  onSave,
  onCancel,
}) => {
  const classes = useStyles()
  const { appendModal, closeAllModals } = useContext(ModalsContext)
  const [notifications, setNotifications] = useState<FormNotification[]>([])
  const [fieldErrors, setFieldErrors] = useState<FieldErrors[]>([])
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [editedNotificationIds, setEditedNotificationIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (existingNotifications.length === 0) {
      setNotifications([createEmptyNotification([])])
    } else {
      setNotifications(existingNotifications.map(toFormNotification))
    }
    setFieldErrors([])
    setEditedNotificationIds(new Set())
  }, [existingNotifications])

  // 編集前（読み込み時）の値
  const beforeEditNotifications = useMemo(
    () => new Map(existingNotifications.map(notification => [notification.id, toFormNotification(notification)])),
    [existingNotifications],
  )

  // 公開終了の行のID。判定は編集前（読み込み時）の値で行う。
  const endedNotificationIds = useMemo(() => {
    const now = new Date()
    return new Set(
      notifications
        .filter(notification => getNotificationStatus(beforeEditNotifications.get(notification.id), now) === 'ended')
        .map(notification => notification.id),
    )
  }, [notifications, beforeEditNotifications])
  const endedCount = endedNotificationIds.size

  const getServerFieldError = (index: number, field: string): string | undefined => {
    return serverErrors.find(e => e.field === `information[${index}].${field}`)?.message
  }

  const validate = (): boolean => {
    const now = new Date()
    const errors = notifications.map(notification =>
      validateNotification(notification, beforeEditNotifications.get(notification.id), now),
    )
    setFieldErrors(errors)
    return errors.every(item => Object.keys(item).length === 0)
  }

  const handleAddNotification = () => {
    const usedIds = notifications.map(n => n.id)
    setNotifications([...notifications, createEmptyNotification(usedIds)])
  }

  const handleDelete = (index: number) => {
    appendModal(
      <PrimaryConfirmModal
        header="お知らせの削除"
        content="このお知らせを削除しますか？"
        label="削除"
        skipButton={true}
        onSubmit={() => {
          closeAllModals()
          onClearServerErrors()
          const deletedId = notifications[index].id
          setNotifications(notifications.filter((_, i) => i !== index))
          setFieldErrors(fieldErrors.filter((_, i) => i !== index))
          setEditedNotificationIds(prev => {
            if (!prev.has(deletedId)) return prev
            const next = new Set(prev)
            next.delete(deletedId)
            return next
          })
        }}
      />,
    )
  }

  const handleBulkDeleteEnded = () => {
    const ended = notifications.filter(notification => endedNotificationIds.has(notification.id))
    if (ended.length === 0) {
      return
    }

    appendModal(
      <PrimaryConfirmModal
        header="公開終了分の一括削除"
        content={
          <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
            {`公開終了済みのお知らせ ${ended.length} 件を削除します。\n\n${buildBulkDeletePreview(
              ended,
            )}\n\nよろしいですか？`}
          </Typography>
        }
        label="削除"
        skipButton={true}
        onSubmit={() => {
          closeAllModals()
          onClearServerErrors()
          const endedIds = new Set(ended.map(notification => notification.id))
          setNotifications(notifications.filter(notification => !endedIds.has(notification.id)))
          setFieldErrors([])
          setEditedNotificationIds(prev => new Set([...prev].filter(id => !endedIds.has(id))))
        }}
      />,
    )
  }

  const handleChange = (index: number, field: keyof FormNotification, value: string | Date | null) => {
    const updated = [...notifications]
    updated[index] = { ...updated[index], [field]: value }
    setNotifications(updated)
    setEditedNotificationIds(prev => {
      if (prev.has(updated[index].id)) return prev
      const next = new Set(prev)
      next.add(updated[index].id)
      return next
    })
  }

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= notifications.length) return

    onClearServerErrors()
    const updatedNotifications = [...notifications]
    const movedNotification = updatedNotifications[index]
    updatedNotifications[index] = updatedNotifications[target]
    updatedNotifications[target] = movedNotification
    setNotifications(updatedNotifications)

    const updatedErrors = [...fieldErrors]
    if (updatedErrors[index] || updatedErrors[target]) {
      const movedError = updatedErrors[index]
      updatedErrors[index] = updatedErrors[target]
      updatedErrors[target] = movedError
      setFieldErrors(updatedErrors)
    }

    setEditedNotificationIds(prev => {
      const next = new Set(prev)
      next.add(updatedNotifications[index].id)
      next.add(updatedNotifications[target].id)
      return next
    })
  }

  const handlePreview = () => {
    if (!validate()) return
    setIsPreviewOpen(true)
  }

  const handleSave = async () => {
    if (!validate()) return
    onSave(notifications.map(toApiNotification))
  }

  const previewNotifications = isPreviewOpen
    ? notifications
        .filter(n => isValidDate(n.startDisplayAt) && isValidDate(n.endDisplayAt))
        .map(toApiNotification)
    : []

  if (isLoading) {
    return (
      <Box className={classes.loadingState}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h5" component="h2">
          お知らせ管理
        </Typography>
      </Box>

      {notifications.map((notification, index) => {
        const publishStatus = getNotificationStatus(notification)
        // 公開終了: 編集不可（削除のみ可）
        const isEnded = endedNotificationIds.has(notification.id)
        return (
          <Paper
            key={notification.id}
            className={`${classes.notificationCard} ${
              editedNotificationIds.has(notification.id) ? classes.notificationCardEdited : ''
            }`}
          >
            <Box className={classes.notificationHeader}>
              <Box className={classes.statusChips}>
                {publishStatus && <Chip label={NOTIFICATION_STATUS_LABELS[publishStatus]} size="small" />}
                {editedNotificationIds.has(notification.id) && (
                  <Chip label="編集済み（未保存）" size="small" color="primary" />
                )}
              </Box>
              <Box>
                <IconButton
                  size="small"
                  onClick={() => handleMove(index, -1)}
                  disabled={index === 0}
                  aria-label="上に移動"
                >
                  <ArrowUpwardIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleMove(index, 1)}
                  disabled={index === notifications.length - 1}
                  aria-label="下に移動"
                >
                  <ArrowDownwardIcon />
                </IconButton>
                <IconButton
                  className={classes.deleteButton}
                  size="small"
                  onClick={() => handleDelete(index)}
                  aria-label="削除"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="ID"
                  value={notification.id}
                  className={classes.formControl}
                  InputProps={{ readOnly: true }}
                  error={!!getServerFieldError(index, 'id')}
                  helperText={getServerFieldError(index, 'id')}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="タイトル"
                  value={notification.title}
                  className={classes.formControl}
                  required
                  error={!!fieldErrors[index]?.title || !!getServerFieldError(index, 'title')}
                  helperText={
                    isEnded ? ENDED_NOTIFICATION_GUIDE : fieldErrors[index]?.title || getServerFieldError(index, 'title')
                  }
                  disabled={isEnded}
                  onChange={e => handleChange(index, 'title', e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="URL"
                  value={notification.url}
                  className={classes.formControl}
                  required
                  multiline
                  maxRows={4}
                  error={!!fieldErrors[index]?.url || !!getServerFieldError(index, 'url')}
                  helperText={
                    isEnded ? ENDED_NOTIFICATION_GUIDE : fieldErrors[index]?.url || getServerFieldError(index, 'url')
                  }
                  disabled={isEnded}
                  onChange={e => handleChange(index, 'url', e.target.value.replace(/\r?\n/g, ''))}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl className={classes.formControl} required disabled={isEnded}>
                  <InputLabel>タイプ</InputLabel>
                  <Select
                    value={notification.type}
                    onChange={e => handleChange(index, 'type', e.target.value as 'information' | 'warning')}
                  >
                    <MenuItem value="information">information</MenuItem>
                    <MenuItem value="warning">warning</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ja}>
                  <KeyboardDateTimePicker
                    label="表示開始日時"
                    value={notification.startDisplayAt}
                    onChange={date => handleChange(index, 'startDisplayAt', date)}
                    format="yyyy/MM/dd HH:mm"
                    className={classes.formControl}
                    error={!!fieldErrors[index]?.startDisplayAt || !!getServerFieldError(index, 'startDisplayAt')}
                    helperText={
                      isEnded
                        ? ENDED_NOTIFICATION_GUIDE
                        : buildDateHelperText(
                            fieldErrors[index]?.startDisplayAt || getServerFieldError(index, 'startDisplayAt'),
                          )
                    }
                    disabled={isEnded}
                    disablePast
                    required
                    ampm={false}
                  />
                </MuiPickersUtilsProvider>
              </Grid>

              <Grid item xs={12} sm={6}>
                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ja}>
                  <KeyboardDateTimePicker
                    label="表示終了日時"
                    value={notification.endDisplayAt}
                    onChange={date => handleChange(index, 'endDisplayAt', date)}
                    format="yyyy/MM/dd HH:mm"
                    className={classes.formControl}
                    error={!!fieldErrors[index]?.endDisplayAt || !!getServerFieldError(index, 'endDisplayAt')}
                    helperText={
                      isEnded
                        ? ENDED_NOTIFICATION_GUIDE
                        : buildDateHelperText(
                            fieldErrors[index]?.endDisplayAt || getServerFieldError(index, 'endDisplayAt'),
                          )
                    }
                    disabled={isEnded}
                    disablePast
                    minDate={isValidDate(notification.startDisplayAt) ? notification.startDisplayAt : undefined}
                    ampm={false}
                    required
                  />
                </MuiPickersUtilsProvider>
              </Grid>
            </Grid>
          </Paper>
        )
      })}

      <Box textAlign="center" className={classes.addButton}>
        <Button variant="outlined" color="primary" startIcon={<AddIcon />} onClick={handleAddNotification}>
          お知らせを追加
        </Button>
      </Box>

      <Box className={classes.buttonGroup}>
        <Button variant="outlined" color="secondary" onClick={handleBulkDeleteEnded} disabled={endedCount === 0}>
          公開終了分を一括削除（{endedCount}件）
        </Button>
        <Box className={classes.actionButtons}>
          <Button variant="outlined" startIcon={<CancelIcon />} onClick={onCancel} disabled={isSaving}>
            キャンセル
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PreviewIcon />}
            onClick={handlePreview}
            disabled={isSaving}
          >
            プレビュー
          </Button>
          <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSave} disabled={isSaving}>
            保存
          </Button>
        </Box>
      </Box>

      <NotificationPreviewModal
        open={isPreviewOpen}
        notifications={previewNotifications}
        onClose={() => setIsPreviewOpen(false)}
      />
    </Box>
  )
}
